import { memo, useEffect, useState } from "react";
import { addDays, differenceInCalendarDays, format, isSameDay, startOfDay } from "date-fns";
import { ko } from "date-fns/locale";
import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OrderDateNoticeProps {
  selectedDate: Date;
  onGoToday: () => void;
}

/** 이 일수를 넘게 벌어지면 날짜 차이를 함께 노출(고착 상태를 눈으로 확인 가능하게). */
const FAR_GAP_DAYS = 90;

/**
 * 자정을 넘기면 갱신되는 '오늘'.
 *
 * render 중 `new Date()`를 읽으면 이 컴포넌트가 memo라 부모 리렌더가 차단되어
 * 자정 이후에도 판정이 영구히 굳는다(밤새 탭을 켜둔 셀러 = 이번 사고 재현 경로).
 * 그래서 자정 타이머 + 탭 복귀(visibilitychange) 재동기를 컴포넌트 안에 둔다.
 * 내부 state 변경은 memo와 무관하게 항상 리렌더되므로 이 조합이 유효하다.
 */
function useTodayStart(): Date {
  const [today, setToday] = useState(() => startOfDay(new Date()));

  useEffect(() => {
    let timer: number;
    // state와 다음 타이머를 반드시 같은 clock 샘플에서 파생시킨다. 둘을 따로 읽으면
    // 23:59:59에 마운트되고 effect가 자정 직후 실행될 때, 날짜는 어제로 굳은 채
    // 다음 자정(약 24시간 뒤)까지 재계산이 없다.
    const syncAndSchedule = () => {
      window.clearTimeout(timer);
      const now = new Date();
      const day = startOfDay(now);
      setToday(prev => prev.getTime() === day.getTime() ? prev : day);
      // 백그라운드 탭에서 스로틀되어 늦게 깨어나도, 깨는 즉시 재계산되므로 안전하다.
      timer = window.setTimeout(syncAndSchedule, startOfDay(addDays(now, 1)).getTime() - now.getTime() + 1000);
    };
    syncAndSchedule();

    // 이 경로가 존재하는 이유 자체가 '기존 타이머가 얼어붙었을 수 있다'이므로,
    // 복귀 시 재계산과 타이머 재무장을 함께 한다(스스로 재개한다고 가정하지 않음).
    const onVisible = () => { if (document.visibilityState === 'visible') syncAndSchedule(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return today;
}

/**
 * 선택된 날짜가 오늘이 아닐 때 띄우는 경고 배너.
 *
 * 배경: `?date=`가 주소창에 무기한 남아(북마크·탭복원·뒤로가기) 과거 날짜에 고착되면,
 * 신규 주문이 그 날짜로 저장된다(orderRepository는 selectedDate를 그대로 insert).
 * 기존 날짜 표시는 CustomerTemplateManager의 회색 소제목 한 줄뿐이고 그마저도
 * detail 뷰 전용이라, batch 뷰에서는 날짜 단서가 아예 없었다.
 * 그래서 이 배너는 뷰 모드 바깥(Home 최상단)에 두어 두 모드 모두를 덮는다.
 *
 * 색은 amber 고정 — 빨강은 킵, 스트라이프는 블랙리스트가 선점했다.
 */
function OrderDateNoticeImpl({ selectedDate, onGoToday }: OrderDateNoticeProps) {
  const today = useTodayStart();
  if (isSameDay(selectedDate, today)) return null;

  const gapDays = differenceInCalendarDays(today, selectedDate);
  const isFar = Math.abs(gapDays) > FAR_GAP_DAYS;
  const gapText = gapDays > 0 ? `오늘보다 ${gapDays}일 전입니다.` : `오늘보다 ${-gapDays}일 뒤입니다.`;

  return (
    <div
      role={isFar ? "alert" : "status"}
      className="mb-3 flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <CalendarClock className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="text-sm leading-relaxed text-amber-900">
          <p className="font-semibold">
            오늘이 아닌 {format(selectedDate, 'yyyy년 M월 d일', { locale: ko })} 주문을 보고 있습니다.
          </p>
          <p className="mt-0.5 text-amber-800">
            지금 추가하는 주문도 이 날짜로 저장됩니다.
            {isFar && ` ${gapText}`}
          </p>
        </div>
      </div>
      <Button
        onClick={onGoToday}
        className="flex-shrink-0 bg-amber-600 text-white hover:bg-amber-700"
      >
        오늘로 이동
      </Button>
    </div>
  );
}

export const OrderDateNotice = memo(OrderDateNoticeImpl);
